/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { forwardRef, Ref, useRef, useLayoutEffect, useState } from 'react';
import { useToast, useButton } from 'react-aria';
import { motion } from 'motion/react';
import { Box } from '@backstage/ui';
import {
  RiInformationLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiAlertLine,
  RiCloseLine,
} from '@remixicon/react';
import type { ToastApiMessageProps } from './types';
import styles from './Toast.module.css';
// eslint-disable-next-line @backstage/no-relative-monorepo-imports
import { BgReset } from '../../../../../packages/ui/src/hooks/useBg';

// Track which toasts are being manually closed (vs auto-timeout)
// This allows different exit animations for each case
const manuallyClosingToasts = new Set<string>();

/**
 * A Toast displays a brief, temporary notification of actions, errors, or other events in an application.
 *
 * @remarks
 * The Toast component is used internally by ToastContainer and managed by a ToastQueue.
 * It supports multiple status variants (neutral, info, success, warning, danger) and can display
 * a title and description. Toasts can be dismissed manually or automatically.
 *
 * @internal
 */
export const Toast = forwardRef(
  (props: ToastApiMessageProps, ref: Ref<HTMLDivElement>) => {
    const {
      toast,
      state,
      index = 0,
      isExpanded = false,
      onClose,
      status,
      expandedY: expandedYProp = 0,
      collapsedHeight,
      naturalHeight,
      onHeightChange,
    } = props;

    // Use internal ref if none provided
    const internalRef = useRef<HTMLDivElement>(null);
    const toastRef = (ref as React.RefObject<HTMLDivElement>) || internalRef;

    // Get ARIA props from useToast hook
    const { toastProps, titleProps, closeButtonProps } = useToast(
      { toast },
      state,
      toastRef,
    );

    // Close button ref for useButton hook
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    // Extract only ARIA and accessibility props from toastProps to avoid
    // conflicts with motion.div's event handler types (motion has its own drag API)
    const ariaProps = {
      role: toastProps.role,
      tabIndex: toastProps.tabIndex,
      'aria-label': toastProps['aria-label'],
      'aria-labelledby': toastProps['aria-labelledby'],
      'aria-describedby': toastProps['aria-describedby'],
      'aria-posinset': toastProps['aria-posinset'],
      'aria-setsize': toastProps['aria-setsize'],
    };

    // Track whether we've measured this toast's natural height
    const [hasMeasured, setHasMeasured] = useState(false);
    // Store the measured natural height locally to avoid re-measurement issues
    const naturalHeightRef = useRef<number | null>(null);

    // Measure this toast's natural height on mount (before paint)
    // Using useLayoutEffect ensures we measure before the browser paints
    useLayoutEffect(() => {
      if (!onHeightChange) return;
      if (naturalHeightRef.current) return; // Already measured

      const element = toastRef.current;
      if (!element) return;

      // Measure immediately - useLayoutEffect runs before paint
      const height = element.getBoundingClientRect().height;
      if (height > 0) {
        naturalHeightRef.current = height;
        onHeightChange(toast.key, height);
        setHasMeasured(true);
      }
    }, [toast.key, onHeightChange, toastRef]);

    // Close button handler
    const handleClose = () => {
      // Mark this toast as manually closed for exit animation
      manuallyClosingToasts.add(toast.key);
      onClose?.();
      state.close(toast.key);
    };

    // Get button props from useButton hook
    const { buttonProps } = useButton(
      {
        'aria-label': closeButtonProps['aria-label'],
        onPress: handleClose,
      },
      closeButtonRef,
    );

    // Get content from toast
    const content = toast.content;
    const finalStatus = status || content.status || 'info';

    // Determine which icon to render based on status
    const getStatusIcon = () => {
      switch (finalStatus) {
        case 'neutral':
          // Neutral status has no icon
          return null;
        case 'success':
          return <RiCheckLine aria-hidden="true" />;
        case 'warning':
          return <RiErrorWarningLine aria-hidden="true" />;
        case 'danger':
          return <RiAlertLine aria-hidden="true" />;
        case 'info':
        default:
          return <RiInformationLine aria-hidden="true" />;
      }
    };

    const statusIcon = getStatusIcon();

    // Calculate stacking values based on index
    // Collapsed: each toast behind scales down 5% and peeks up 12px
    const collapsedScale = Math.max(0.85, 1 - index * 0.05);
    const collapsedY = -index * 12;

    // Use expanded or collapsed values based on hover state
    // expandedYProp is pre-calculated based on actual toast heights
    const animateY = isExpanded ? expandedYProp : collapsedY;
    const animateScale = isExpanded ? 1 : collapsedScale;
    const stackZIndex = 1000 - index;

    // Check if this toast is being manually closed
    const isManualClose = manuallyClosingToasts.has(toast.key);

    // Different exit animations for manual close vs auto-timeout
    // Manual close: slide down from front, stay on top
    // Auto-timeout: fade out in place, stay in stack position
    const exitAnimation = isManualClose
      ? { opacity: 0, y: 100, scale: 1, zIndex: 2000 }
      : {
          opacity: 0,
          y: animateY + 50,
          scale: animateScale,
          zIndex: stackZIndex,
        };

    // Height animation for back toasts:
    // - Front toast (index 0): never set height, uses natural CSS height
    // - Back toasts: animate between collapsedHeight and their own naturalHeight
    const measuredHeight = naturalHeight || naturalHeightRef.current;
    const isBackToast = index > 0;
    const hasValidMeasurements =
      hasMeasured && collapsedHeight && measuredHeight;

    // For back toasts with valid measurements, calculate target height
    // Otherwise, let CSS handle it naturally
    const animateProps: {
      opacity: number;
      y: number;
      scale: number;
      zIndex: number;
      height?: number;
    } = {
      opacity: 1,
      y: animateY,
      scale: animateScale,
      zIndex: stackZIndex,
      ...(isBackToast && hasValidMeasurements
        ? { height: isExpanded ? measuredHeight : collapsedHeight }
        : {}),
    };

    const shouldClipContent =
      isBackToast && hasValidMeasurements && !isExpanded;

    return (
      <motion.div
        {...ariaProps}
        ref={toastRef}
        className={styles.toast}
        style={
          {
            '--toast-index': index,
            overflow: shouldClipContent ? 'hidden' : undefined,
          } as React.CSSProperties
        }
        initial={{ opacity: 0, y: 100, scale: 1 }}
        animate={animateProps}
        exit={exitAnimation}
        onAnimationComplete={definition => {
          // Clean up the manual close tracking after exit animation
          if (definition === 'exit') {
            manuallyClosingToasts.delete(toast.key);
          }
        }}
        transition={{ type: 'spring', stiffness: 400, damping: 35 }}
        data-status={finalStatus}
      >
        <BgReset>
          <Box className={styles.surface}>
            <div className={styles.wrapper}>
              {statusIcon && <div className={styles.icon}>{statusIcon}</div>}
              <div className={styles.content}>
                <div {...titleProps} className={styles.title}>
                  {content.title}
                </div>
                {content.description && (
                  <div className={styles.description}>
                    {content.description}
                  </div>
                )}
                {content.links && content.links.length > 0 && (
                  <div className={styles.links}>
                    {content.links.map(link => (
                      <a key={link.href} href={link.href}>
                        {link.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* eslint-disable-next-line react/forbid-elements */}
            <button
              {...buttonProps}
              ref={closeButtonRef}
              className={styles.closeButton}
            >
              <RiCloseLine aria-hidden="true" />
            </button>
          </Box>
        </BgReset>
      </motion.div>
    );
  },
);

Toast.displayName = 'Toast';
